/* eslint-disable local-rules/no-loose-string */

/**
 * Client half of the dev-server store seam — thin wrappers over the
 * `/dev/tweaks/context-state` route. Dev-only code paths (the provider) are
 * the only callers; production never executes these. Raw same-origin `fetch`
 * mirrors the v1 panel-state seam: this is internal dev tooling talking to
 * the Next dev server itself, not a BL API call, so the axios `apiClient`
 * (auth interceptor, BL base URL, retry) does not apply.
 */

import { TweaksStoreOp } from "./types";
import type { TweakContextFile, TweaksRegistrationMark } from "./types";

const CONTEXT_STATE_ENDPOINT = "/dev/tweaks/context-state";

interface ContextStateResponse {
  file: TweakContextFile | null;
}

export async function fetchContextFile(
  context: string,
): Promise<TweakContextFile | null> {
  const response = await fetch(
    `${CONTEXT_STATE_ENDPOINT}?context=${encodeURIComponent(context)}`,
    { cache: "no-store" },
  );
  if (!response.ok) {
    throw new Error(`Unable to load tweaks context "${context}"`);
  }
  const payload = (await response.json()) as ContextStateResponse;
  return payload.file;
}

export async function registerContextMarks({
  context,
  marks,
}: {
  context: string;
  marks: TweaksRegistrationMark[];
}): Promise<TweakContextFile> {
  const response = await fetch(CONTEXT_STATE_ENDPOINT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ op: TweaksStoreOp.Register, context, marks }),
  });
  if (!response.ok) {
    throw new Error(`Unable to register tweaks context "${context}"`);
  }
  const payload = (await response.json()) as ContextStateResponse;
  if (!payload.file) {
    throw new Error(`Registration for "${context}" returned no file`);
  }
  return payload.file;
}
